import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Cc7AuthService, Cc7Error, Cc7LogoutResult, localize } from "@rinami/cc7-ng";
import { Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { catchError, EMPTY, map, Observable, tap } from "rxjs";

const LOGIN_ROUTE = "/security/login";
const POST_LOGOUT_REDIRECT_PARAM = "post_logout_redirect_uri";

export interface LogoutOptions {
  /**
   * When `true`, after the regular app logout the browser is sent to the identity provider's logout page
   * (the `logoutUrl` Cantara returns), which then redirects back to this app's login page.
   * Falls back to the regular redirect to the login page when Cantara returned no `logoutUrl`.
   */
  ssoLogout?: boolean;
}

@Injectable({ providedIn: "root" })
export class LogoutWorker {
  private readonly authService = inject(Cc7AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);

  logout(options: LogoutOptions = {}): Observable<void> {
    return this.authService.logout().pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((result: Cc7LogoutResult) => {
        this.clearSessionState();
        const ssoLogoutUrl = options.ssoLogout ? this.buildSsoLogoutUrl(result.response?.logoutUrl) : null;
        if (ssoLogoutUrl) {
          window.location.assign(ssoLogoutUrl);
          return;
        }
        void this.router.navigate([LOGIN_ROUTE]);
      }),
      map(() => void 0),
      catchError((err: unknown) => {
        this.messageService.add({
          severity: "error",
          summary: err instanceof Cc7Error ? err.title : localize("common.error"),
          detail: err instanceof Cc7Error ? err.description : localize("inspectionList.label.logoutError"),
        });
        this.clearSessionState();
        void this.router.navigate([LOGIN_ROUTE]);
        return EMPTY;
      })
    );
  }

  /**
   * Adds this app's login page as the identity provider's post-logout redirect, so that after the SSO
   * logout the user lands back on the app's login page. A `post_logout_redirect_uri` already present on
   * the URL Cantara returned is left untouched. Returns `null` for a missing/invalid URL.
   */
  private buildSsoLogoutUrl(logoutUrl: string | undefined): string | null {
    if (!logoutUrl) {
      return null;
    }
    try {
      const url = new URL(logoutUrl);
      if (url.protocol !== "https:" && url.protocol !== "http:") {
        return null;
      }
      if (!url.searchParams.has(POST_LOGOUT_REDIRECT_PARAM)) {
        const loginUrl = new URL(this.router.serializeUrl(this.router.createUrlTree([LOGIN_ROUTE])), document.baseURI);
        url.searchParams.set(POST_LOGOUT_REDIRECT_PARAM, loginUrl.href);
      }
      return url.href;
    } catch {
      return null;
    }
  }

  /**
   * Clears all per-session UI state persisted to sessionStorage (selected equipment on
   * inspection-list, in-progress work request draft, active checklist, etc.).
   *
   * Logout must always be treated as the end of the session: whichever screen/equipment/draft
   * the user was on is intentionally discarded, so the next login always starts from a
   * completely empty Inspection List - regardless of what was open when they logged out.
   *
   * Also invoked when a login identity change is detected (see `Home.ngOnInit()`), since
   * switching user/tenant/config must discard the previous identity's UI state the same way
   * a logout does - otherwise stale selections (e.g. selected equipment) could leak across
   * identities.
   */
  clearSessionState(): void {
    try {
      sessionStorage.clear();
    } catch {
      // sessionStorage may be unavailable (e.g. private browsing); nothing to clean up in that case.
    }
  }
}
